/**
 * The one place an organization's own admin hears about a bill inside RosterPilot.
 *
 * It sits above every dashboard shell, so an upcoming invoice reaches the person who pays it
 * wherever they happen to be working, rather than only in an email an athletics department's AP
 * system may have filed somewhere. Everything it shows is decided by the server
 * (`GET /api/billing/me`): which invoice, whether a dismissal still applies, and whether being
 * overdue has overridden that dismissal. This component formats and dismisses, and decides nothing.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { dismissBillingNotice, getMyBillingNotice } from '../../services/billing';
import { formatDateOnly, formatMoney } from '../admin/billing/billingFormat';
import { NO_BILLING_NOTICE, type MyBillingNotice } from '../../types/billing';
import './BillingNoticeBanner.css';

/**
 * The last answer, so moving between shells does not flash the banner away and back.
 *
 * `DashboardLayout` wraps five separate layouts, and navigating from the dashboard to settings
 * unmounts one and mounts another. Without this, an admin with an overdue invoice watched the
 * warning disappear for the length of a round trip every time they changed section.
 *
 * Keyed by user and organization together, so nothing survives a logout, and a system admin
 * switching organizations is not shown the last one's bill while the new one loads.
 */
let cache: { key: string; notice: MyBillingNotice } | null = null;

/** Only these two roles can reach the endpoint at all; a coach asking would get a 403. */
const BILLING_ROLES = new Set(['admin', 'system_admin']);

export const BillingNoticeBanner = () => {
  const { user, effectiveOrganizationId, organizationPaused } = useAuth();

  const role = user?.profile?.role;
  const userId = user?.profile?.id ?? null;
  const organizationId = effectiveOrganizationId ?? user?.profile?.organizationId ?? null;

  // A paused organization is already looking at the pause screen, which says more about the
  // situation than a banner could. Asking anyway would be a second explanation of the same thing.
  const canSee = Boolean(role && BILLING_ROLES.has(role) && userId && organizationId)
    && !organizationPaused;

  const key = `${userId ?? ''}:${organizationId ?? ''}`;

  const [notice, setNotice] = useState<MyBillingNotice>(
    () => (cache?.key === key ? cache.notice : NO_BILLING_NOTICE),
  );
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!canSee) {
      setNotice(NO_BILLING_NOTICE);
      return;
    }

    // Shown from the cache while this runs, so the refetch is invisible unless the answer changed.
    setNotice(cache?.key === key ? cache.notice : NO_BILLING_NOTICE);

    let active = true;

    getMyBillingNotice()
      .then((next) => {
        cache = { key, notice: next };
        if (active) setNotice(next);
      })
      .catch(() => {
        // Deliberately silent. The request already reports itself through the API client, and a
        // banner that cannot load is a banner that does not appear. Turning it into an error
        // message would put a billing failure in front of a coach in the middle of their work,
        // about something they cannot act on.
        if (active) setNotice(NO_BILLING_NOTICE);
      });

    return () => {
      active = false;
    };
  }, [canSee, key]);

  const handleDismiss = useCallback(async () => {
    if (!notice.invoiceId || dismissing) return;

    setDismissing(true);
    try {
      const next = await dismissBillingNotice(notice.invoiceId);
      cache = { key, notice: next };
      setNotice(next);
    } catch {
      // It stays on screen. That is the honest outcome of a dismissal that did not save, and it
      // comes back on the next page anyway, so pretending it worked would be the worse lie.
    } finally {
      setDismissing(false);
    }
  }, [dismissing, key, notice.invoiceId]);

  const shown = canSee && notice.hasBill;

  /*
   * The banner's own height, published to the document element so the sticky sidebar can subtract
   * it (see DashboardLayout.css). Measured rather than hardcoded because the text wraps to two
   * lines on a narrow window, and observed rather than measured once for the same reason.
   */
  const bannerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const root = document.documentElement;

    const clear = () => root.style.removeProperty('--app-notice-height');

    if (!shown) {
      clear();
      return clear;
    }

    const publish = () => {
      const height = bannerRef.current?.offsetHeight ?? 0;
      root.style.setProperty('--app-notice-height', `${height}px`);
    };

    publish();

    // Absent in some test environments, and an unobserved banner is still a correctly sized one at
    // the width it was measured at, so this is an enhancement rather than a requirement.
    if (typeof ResizeObserver === 'undefined' || !bannerRef.current) return clear;

    const observer = new ResizeObserver(publish);
    observer.observe(bannerRef.current);

    return () => {
      observer.disconnect();
      clear();
    };
  }, [shown, notice.invoiceId, notice.isOverdue]);

  if (!shown) return null;

  const amount = formatMoney(notice.amountDueCents, notice.currency ?? 'usd');
  const due = formatDateOnly(notice.dueOn);
  const reference = notice.number;

  const heading = notice.isOverdue
    ? 'Your RosterPilot invoice is past due'
    : 'You have a RosterPilot invoice coming up';

  const detail = notice.isOverdue
    ? [
        `${amount} was due${due ? ` on ${due}` : ''}.`,
        notice.daysOverdue > 0
          ? `That is ${notice.daysOverdue} ${notice.daysOverdue === 1 ? 'day' : 'days'} ago.`
          : null,
      ]
        .filter(Boolean)
        .join(' ')
    : `${amount} is due${due ? ` on ${due}` : ''}.`;

  return (
    <section
      ref={bannerRef}
      className={`billing-notice${notice.isOverdue ? ' billing-notice--overdue' : ''}`}
      aria-label="Billing notice"
    >
      <div className="billing-notice__body" role="status">
        <p className="billing-notice__heading">{heading}</p>
        <p className="billing-notice__detail">
          {detail}
          {reference ? <span className="billing-notice__reference"> Reference {reference}.</span> : null}
        </p>
      </div>

      <div className="billing-notice__actions">
        {notice.hostedInvoiceUrl ? (
          <a
            className="billing-notice__pay"
            href={notice.hostedInvoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View and pay
          </a>
        ) : null}

        {notice.canDismiss ? (
          <button
            type="button"
            className="billing-notice__dismiss"
            onClick={handleDismiss}
            disabled={dismissing}
          >
            Not now
          </button>
        ) : null}
      </div>
    </section>
  );
};
